import { useCallback, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import useDebouncedCallback from '../../../../hooks/use-debounced-callback'
import { FormField, Select } from '../../../../components/inputs'
import { Button } from '../../../../components/button'
import useManualEntryOptions from './use-manual-entry-options'

export default function ManualEntry({ vehicle, prePopulate, onSubmit }) {
  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' })
  const [carDetails, setCarDetails] = useState({
    year: prePopulate ? vehicle?.year : null,
    makeId: prePopulate ? vehicle?.makeId : null,
    modelId: prePopulate ? vehicle?.modelId : null,
    shortTrimName: null,
  })

  const handleYearChange = useDebouncedCallback((value) => {
    setCarDetails((prev) => ({
      ...prev,
      year: +value,
      makeId: null,
      modelId: null,
      shortTrimName: null,
    }))
  }, 500)

  const handleSelectChange = useCallback((newValue, { name }) => {
    if (name === 'makeId') {
      return setCarDetails((prev) => ({
        ...prev,
        modelId: null,
        shortTrimName: null,
        [name]: newValue.value,
      }))
    }

    if (name === 'modelId') {
      return setCarDetails((prev) => ({ ...prev, shortTrimName: null, [name]: newValue.value }))
    }

    setCarDetails((prev) => ({ ...prev, [name]: newValue.value }))
  }, [])

  const {
    makeOptions,
    isMakesFetching,
    modelOptions,
    isModelsFetching,
    shortTrimOptions,
    isShortTrimsFetching,
  } = useManualEntryOptions({
    year: carDetails.year,
    makeId: carDetails.makeId,
    modelId: carDetails.modelId,
  })

  const handleSubmit = (event) => {
    event.preventDefault()

    const make = makeOptions.find((make) => make.value === carDetails.makeId)
    const model = modelOptions.find((model) => model.value === carDetails.modelId)
    const shortTrimName = shortTrimOptions.find((trim) => trim.value === carDetails.shortTrimName)

    onSubmit({
      year: carDetails.year,
      makeId: make.value,
      makeName: make.label,
      modelId: model.value,
      modelName: model.label,
      shortTrimName: shortTrimName.value,
      ...(vehicle && vehicle.vin ? { vin: vehicle.vin } : {}),
    })
  }

  const continueBtnDisabled = Object.keys(carDetails).some((key) => carDetails[key] == null)

  return (
    <div className='manualEntryView'>
      <h1 className='title'>Enter vehicle details</h1>
      <form onSubmit={handleSubmit}>
        <div className='inputContainer'>
          <FormField
            aria-label='year'
            type='tel'
            name='year'
            placeholder='Year'
            defaultValue={carDetails.year}
            onChange={(e) => handleYearChange(e.target.value)}
          />
        </div>
        <div className='inputContainer'>
          <Select
            isLoading={isMakesFetching}
            aria-label='make'
            name='makeId'
            placeholder='Make'
            value={makeOptions.find(({ value }) => +value === carDetails.makeId) || null}
            options={makeOptions}
            menuPlacement={isMobile ? 'top' : 'bottom'}
            maxMenuHeight={200}
            onChange={handleSelectChange}
          />
        </div>
        <div className='inputContainer'>
          <Select
            isLoading={isModelsFetching}
            aria-label='model'
            name='modelId'
            placeholder='Model'
            value={modelOptions.find(({ value }) => +value === carDetails.modelId) || null}
            options={modelOptions}
            menuPlacement={isMobile ? 'top' : 'bottom'}
            maxMenuHeight={150}
            onChange={handleSelectChange}
          />
        </div>
        <div className='inputContainer'>
          <Select
            isLoading={isShortTrimsFetching}
            aria-label='shortTrimName'
            name='shortTrimName'
            placeholder='Trim'
            value={shortTrimOptions.find(({ value }) => value === carDetails.shortTrimName) || null}
            options={shortTrimOptions}
            menuPlacement='top'
            maxMenuHeight={200}
            onChange={handleSelectChange}
          />
        </div>
        <div className='buttonsContainer'>
          <Button type='submit' disabled={continueBtnDisabled}>
            Continue
          </Button>
        </div>
      </form>

      <style jsx>{`
        .manualEntryView {
          max-width: 100vw;
          width: 43.6rem;
          background: var(--primary-bg-color);
        }

        .title {
          padding: 3.4rem 0 0;
          text-align: center;
          font-size: 3.4rem;
        }

        form {
          max-width: 39.7rem;
          width: 100%;
          margin: 0 auto;
          padding: 1.6rem 0 4.1rem;
        }

        .inputContainer {
          margin: 0 0 2.4rem;

          &.vin {
            margin: 0 0 3.1rem;
          }
        }

        .buttonsContainer {
          margin: 0.8rem 0 0;
          text-align: center;
        }

        @media (max-width: 1024px) {
          .manualEntryView {
            max-width: 34.7rem;
            width: 100%;
            margin: 0 auto;
            height: 100%;
          }

          .title {
            padding: 6rem 0 0;
            font-size: 1.8rem;
          }

          form {
            max-width: 100%;
            padding: 2.6rem 0;
          }

          .buttonsContainer {
            position: fixed;
            left: 0;
            bottom: 2.1rem;
            width: 100%;
            padding: 0 1.2rem;

            :global(a) {
              width: 100%;
            }
          }
        }
      `}</style>
    </div>
  )
}
